import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApproveRequest, RejectRequest } from '@trackkm/shared';
import { api } from '@/lib/api';

export function usePendingApprovals(page?: number) {
  return useQuery({
    queryKey: ['approvals', 'pending', page],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (page) params.page = String(page);
      const response = await api.get<unknown[]>('/approvals/pending', params);
      return response;
    },
  });
}

export function usePendingCount() {
  return useQuery({
    queryKey: ['approvals', 'pending-count'],
    queryFn: async () => {
      const response = await api.get<{ count: number }>('/approvals/pending/count');
      return response.data?.count ?? 0;
    },
    refetchInterval: 60_000, // refresh every minute
  });
}

export function useApproveTrip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApproveRequest) => {
      const response = await api.post<unknown>(`/approvals/${data.tripId}/approve`, { note: data.note });
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to approve trip');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
    },
  });
}

export function useRejectTrip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RejectRequest) => {
      const response = await api.post<unknown>(`/approvals/${data.tripId}/reject`, { note: data.note });
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to reject trip');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
    },
  });
}
